/**
 * `<gs-cc-widget>` — Content projector for internal community widgets.
 *
 * Projects server-rendered content from a `<template shadowrootmode>` into
 * shadow DOM, injects design tokens, and hydrates Preact components.
 * Used standalone (when content is SSR'd) or inside `<gs-cc-embed-widget>`
 * (which fetches the content from the backend).
 */
export class WidgetElement extends HTMLElement {
  private pendingReadyHandler: (() => void) | null = null

  connectedCallback(): void {
    if (this.shadowRoot) {
      WidgetElement.injectDesignTokens(this.shadowRoot)
      this.hydratePreact(this.shadowRoot)
      return
    }

    const template = this.querySelector<HTMLTemplateElement>('template[shadowrootmode]')

    if (!template) {
      console.warn('[WidgetSDK] Widget: No shadow root or template found.')
      return
    }

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = this.attachShadow({ mode })
    shadowRoot.appendChild(template.content.cloneNode(true))
    template.remove()

    WidgetElement.injectDesignTokens(shadowRoot)
    this.hydratePreact(shadowRoot)
  }

  disconnectedCallback(): void {
    if (this.pendingReadyHandler) {
      document.removeEventListener('insidedReady', this.pendingReadyHandler)
      this.pendingReadyHandler = null
    }
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = ''
    }
  }

  static injectDesignTokens(shadowRoot: ShadowRoot): void {
    WidgetElement.injectHeadResources(shadowRoot)
    // Only fall back to document.head resources when NOT in embed mode —
    // embed widgets get their resources from the resource-loader.
    if (!(window as any).__insidedEmbed?.headResources) {
      WidgetElement.injectDesignTokensFromDocument(shadowRoot)
    }
  }

  /**
   * Embed path: the resource-loader stores parsed head resources (stylesheets, styles)
   * in window.__insidedEmbed.headResources. Inject them into the shadow root so the
   * widget renders with the correct fonts, CSS variables and custom styles.
   *
   * External font stylesheets (different base domain than the community) are also
   * added to document.head because @font-face rules loaded exclusively inside
   * shadow DOM are not reliably registered globally across all browsers.
   */
  private static injectHeadResources(shadowRoot: ShadowRoot): void {
    const res = (window as any).__insidedEmbed?.headResources as
      | { stylesheets?: string[]; styles?: Array<{ id?: string; content: string }> }
      | undefined
    if (!res) return

    for (const href of res.stylesheets ?? []) {
      if (!shadowRoot.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = href
        shadowRoot.prepend(link)
      }

      // Font stylesheets also need to be in document.head so that @font-face
      // rules are registered globally. Non-font stylesheets (e.g. preact-app.css)
      // are kept scoped to the shadow root to avoid polluting the host page.
      if (WidgetElement.isFontStylesheet(href) && !document.head.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = href
        document.head.appendChild(link)
      }
    }

    for (const { id, content } of res.styles ?? []) {
      if (id && shadowRoot.getElementById(id)) continue
      const style = document.createElement('style')
      if (id) style.id = id
      style.textContent = id === 'css-variables' ? content.replace(/html\s*\{/, ':host {') : content
      shadowRoot.prepend(style)
    }
  }

  /** Whitelisted font CDN hostnames whose stylesheets need document-level @font-face registration. */
  private static readonly FONT_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com']

  private static isFontStylesheet(href: string): boolean {
    try {
      return WidgetElement.FONT_HOSTS.includes(new URL(href).hostname)
    } catch {
      return false
    }
  }

  /**
   * Full community page fallback: css-variables and font links are in document.head
   * from server-rendered templates. Copy them into the shadow root if not already present.
   */
  private static injectDesignTokensFromDocument(shadowRoot: ShadowRoot): void {
    if (!shadowRoot.getElementById('css-variables')) {
      const source = document.getElementById('css-variables')
      if (source instanceof HTMLStyleElement && source.textContent) {
        const style = document.createElement('style')
        style.id = 'css-variables'
        style.textContent = source.textContent.replace(/html\s*\{/, ':host {')
        shadowRoot.prepend(style)
      }
    }

    const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    for (const link of links) {
      const href = link.getAttribute('href')
      if (!href) continue
      if (shadowRoot.querySelector(`link[href="${href}"]`)) continue
      shadowRoot.prepend(link.cloneNode(true))
    }
  }

  private hydratePreact(root: ShadowRoot): void {
    const insided = (globalThis as any).insided
    if (typeof insided?.bindPreactComponents === 'function') {
      insided.bindPreactComponents(root)
      return
    }

    // Resources not loaded yet — wait for the Preact runtime to signal readiness.
    // bootstrap.ts dispatches 'insidedReady' on document after initializeApp()
    // sets window.insided.bindPreactComponents.
    this.pendingReadyHandler = (): void => {
      this.pendingReadyHandler = null
      const ready = (globalThis as any).insided
      if (typeof ready?.bindPreactComponents === 'function') {
        ready.bindPreactComponents(root)
      }
    }
    document.addEventListener('insidedReady', this.pendingReadyHandler, { once: true })
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('gs-cc-widget')) {
  customElements.define('gs-cc-widget', WidgetElement)
}
